import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { BACKEND_NODE_URL } from '../config/constants';
import LoadingSpinner from './LoadingSpinner';
import './MyPlanTab.css';
import AppIcon from './icons/AppIcon';

const MyPlanTab = () => {
  const { user, reloadAuth, isAuthInitialized } = useAuth();
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);
  const [credits, setCredits] = useState(null);
  const [usageLimits, setUsageLimits] = useState(null);
  const [error, setError] = useState('');

  const loadPlanData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let currentUser = user;

      if (!currentUser?.id && reloadAuth) {
        try {
          await reloadAuth();
          currentUser = user;
        } catch (reloadError) {
          console.warn('[MyPlanTab] Falha ao recarregar auth:', reloadError);
        }
      }

      if (!currentUser?.id) {
        setPlanData(null);
        setCredits(null);
        setError('Não foi possível identificar o usuário autenticado.');
        return;
      }

      const params = new URLSearchParams({ userId: currentUser.id });
      if (currentUser.company_id) {
        params.append('companyId', currentUser.company_id);
      }

      const response = await fetch(`${BACKEND_NODE_URL}/api/company/plan?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Falha ao carregar plano.');
      }

      const { subscription, credits: creditsData } = await response.json();

      if (!subscription) {
        setPlanData(null);
        setCredits(null);
        setUsageLimits(null);
        setError('Nenhum plano encontrado para esta empresa.');
        return;
      }

      setPlanData(subscription);
      setCredits(creditsData || null);

      const companyId = subscription.company_id;

      // Sincronizar usage_limits com dados reais
      if (companyId) {
        try {
          await supabase.rpc('sync_usage_limits', { p_company_id: companyId });
        } catch (syncError) {
          console.warn('[MyPlanTab] Erro ao sincronizar usage_limits:', syncError?.message || syncError);
        }
      }

      // Verificar e renovar créditos se necessário
      if (companyId) {
        try {
          await supabase.rpc('check_and_renew_credits', { p_company_id: companyId });
        } catch (renewError) {
          console.warn('[MyPlanTab] Erro ao renovar créditos:', renewError?.message || renewError);
        }
      }

      // Buscar limites de uso
      if (companyId) {
        const { data: limitsData, error: limitsError } = await supabase
          .from('usage_limits')
          .select('*')
          .eq('company_id', companyId)
          .single();

        if (!limitsError && limitsData) {
          setUsageLimits(limitsData);
        }
      }

    } catch (error) {
      console.error('Erro ao carregar dados do plano:', error);
      setPlanData(null);
      setCredits(null);
      setUsageLimits(null);
      setError(error?.message || 'Erro ao carregar plano.');
    } finally {
      setLoading(false);
    }
  }, [reloadAuth, user]);

  useEffect(() => {
    if (isAuthInitialized) {
      loadPlanData();
    }
  }, [isAuthInitialized, loadPlanData]);

  if (loading) {
    return <LoadingSpinner message="Carregando informações do plano..." />;
  }

  if (!planData) {
    return (
      <div className="my-plan-empty">
        <AppIcon name="info" />
        <p>{error || 'Nenhum plano ativo encontrado'}</p>
      </div>
    );
  }

  const plan = planData.plans;
  const usagePercent = credits && credits.credits_total
    ? (credits.credits_used / credits.credits_total) * 100
    : 0;

  return (
    <div className="my-plan-tab">
      {/* Header do Plano */}
      <div className="plan-header">
        <div className="plan-header-left">
          <div className="plan-icon">
            <AppIcon name="workspace_premium" />
          </div>
          <div className="plan-info">
            <h2>{plan.name}</h2>
            <p className="plan-description">{plan.description || 'Seu plano atual'}</p>
          </div>
        </div>
        <div className="plan-header-right">
          <div className="plan-price">
            <span className="price-value">R$ {parseFloat(plan.price_brl).toFixed(2)}</span>
            <span className="price-period">/mês</span>
          </div>
          {plan.slug !== 'admin' && (
            <button className="btn-upgrade">
              <AppIcon name="upgrade" />
              Fazer Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Créditos */}
      {credits && (
        <div className="plan-section">
          <div className="section-header">
            <AppIcon name="account_balance_wallet" />
            <h3>Créditos</h3>
          </div>
          
          <div className="credits-grid">
            <div className="credit-card">
              <span className="credit-label">Disponíveis</span>
              <span className="credit-value">{credits.credits_available.toLocaleString('pt-BR')}</span>
            </div>
            <div className="credit-card">
              <span className="credit-label">Usados</span>
              <span className="credit-value used">{credits.credits_used.toLocaleString('pt-BR')}</span>
            </div>
            <div className="credit-card">
              <span className="credit-label">Total</span>
              <span className="credit-value">{credits.credits_total.toLocaleString('pt-BR')}</span>
            </div>
            {credits.credits_bonus > 0 && (
              <div className="credit-card bonus">
                <span className="credit-label">Bônus</span>
                <span className="credit-value">+{credits.credits_bonus.toLocaleString('pt-BR')}</span>
              </div>
            )}
          </div>

          <div className="credits-progress">
            <div className="progress-header">
              <span>Uso de Créditos</span>
              <span className="progress-percent">{usagePercent.toFixed(1)}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className={`progress-fill ${usagePercent >= 90 ? 'critical' : usagePercent >= 80 ? 'warning' : ''}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <div className="progress-footer">
              <span>Renova em: {new Date(credits.period_end).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Limites de Recursos */}
      {usageLimits && (
        <div className="plan-section">
          <div className="section-header">
            <AppIcon name="analytics" />
            <h3>Uso de Recursos</h3>
          </div>

          <div className="resources-grid">
            <div className="resource-card">
              <div className="resource-icon whatsapp">
                <AppIcon name="chat" />
              </div>
              <div className="resource-info">
                <span className="resource-label">Conexões WhatsApp</span>
                <div className="resource-usage">
                  <span className="usage-current">{usageLimits.current_whatsapp_connections}</span>
                  <span className="usage-separator">/</span>
                  <span className="usage-max">
                    {usageLimits.max_whatsapp_connections === 999999 ? '∞' : usageLimits.max_whatsapp_connections}
                  </span>
                </div>
                <div className="resource-bar">
                  <div 
                    className="resource-fill"
                    style={{ 
                      width: usageLimits.max_whatsapp_connections === 999999 
                        ? '0%' 
                        : `${(usageLimits.current_whatsapp_connections / usageLimits.max_whatsapp_connections) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon agents">
                <AppIcon name="smart_toy" />
              </div>
              <div className="resource-info">
                <span className="resource-label">Agentes IA</span>
                <div className="resource-usage">
                  <span className="usage-current">{usageLimits.current_agents}</span>
                  <span className="usage-separator">/</span>
                  <span className="usage-max">
                    {usageLimits.max_agents === 999999 ? '∞' : usageLimits.max_agents}
                  </span>
                </div>
                <div className="resource-bar">
                  <div 
                    className="resource-fill"
                    style={{ 
                      width: usageLimits.max_agents === 999999 
                        ? '0%' 
                        : `${(usageLimits.current_agents / usageLimits.max_agents) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon projects">
                <AppIcon name="dashboard" />
              </div>
              <div className="resource-info">
                <span className="resource-label">Projetos</span>
                <div className="resource-usage">
                  <span className="usage-current">{usageLimits.current_projects}</span>
                  <span className="usage-separator">/</span>
                  <span className="usage-max">
                    {usageLimits.max_projects === 999999 ? '∞' : usageLimits.max_projects}
                  </span>
                </div>
                <div className="resource-bar">
                  <div 
                    className="resource-fill"
                    style={{ 
                      width: usageLimits.max_projects === 999999 
                        ? '0%' 
                        : `${(usageLimits.current_projects / usageLimits.max_projects) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon users">
                <AppIcon name="group" />
              </div>
              <div className="resource-info">
                <span className="resource-label">Usuários</span>
                <div className="resource-usage">
                  <span className="usage-current">{usageLimits.current_users}</span>
                  <span className="usage-separator">/</span>
                  <span className="usage-max">
                    {usageLimits.max_users === 999999 ? '∞' : usageLimits.max_users}
                  </span>
                </div>
                <div className="resource-bar">
                  <div 
                    className="resource-fill"
                    style={{ 
                      width: usageLimits.max_users === 999999 
                        ? '0%' 
                        : `${(usageLimits.current_users / usageLimits.max_users) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features do Plano */}
      {plan.features && plan.features.length > 0 && (
        <div className="plan-section">
          <div className="section-header">
            <AppIcon name="check_circle" />
            <h3>Recursos Incluídos</h3>
          </div>

          <div className="features-list">
            {plan.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <AppIcon name="check" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Informações da Assinatura */}
      <div className="plan-section">
        <div className="section-header">
          <AppIcon name="info" />
          <h3>Informações da Assinatura</h3>
        </div>

        <div className="subscription-info">
          <div className="info-row">
            <span className="info-label">Status:</span>
            <span className={`info-value status-${planData.status}`}>
              {planData.status === 'active' ? 'Ativo' : planData.status}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Início:</span>
            <span className="info-value">
              {new Date(planData.started_at).toLocaleDateString('pt-BR')}
            </span>
          </div>
          {planData.expires_at && (
            <div className="info-row">
              <span className="info-label">Próxima renovação:</span>
              <span className="info-value">
                {new Date(planData.expires_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Renovação automática:</span>
            <span className="info-value">
              {planData.auto_renew ? 'Sim' : 'Não'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPlanTab;
